import { DOCUMENT, inject, Service } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { SwUpdate } from '@angular/service-worker';
import { filter, fromEvent } from 'rxjs';
import { UPDATE_BOOT_GRACE_MS } from '../constants/app-update.constant';

/**
 * Keeps the trader on the latest deployed build rather than the one the service
 * worker cached.
 *
 * Left alone, the worker serves the cached build and only moves a client to a new one
 * once every tab of the app has been closed — which an installed PWA, resumed from the
 * background for days on end, rarely is. So:
 *
 * - The worker announces a downloaded build with `VERSION_READY`. Right after the page
 *   loaded (see `UPDATE_BOOT_GRACE_MS`), or while the app is in the background, the
 *   page reloads onto it at once. Otherwise the reload waits until the app is next
 *   hidden, so it never pulls a screen out from under a trader who is using it.
 * - The worker is asked for a new build once, as the app initializes, so a deploy is
 *   picked up on the launch that follows it. An installed PWA resumed from the
 *   background is not re-initialized, so it is not asked again until the next launch.
 * - A cached build the worker can no longer serve (`unrecoverable`) is reloaded away
 *   from immediately: there is nothing left to keep the trader on.
 *
 * Instantiated at startup (see `EVENT_DRIVEN_STARTUP`). Inert where the worker is
 * disabled — the `development-*` configurations.
 */
@Service()
export class AppAutoUpdateService {
	private readonly doc = inject(DOCUMENT);
	private readonly swUpdate = inject(SwUpdate);

	/** A new build is ready and waiting for the app to be hidden before it is loaded. */
	private reloadPending = false;

	constructor() {
		if (!this.swUpdate.isEnabled) {
			return;
		}

		this.swUpdate.versionUpdates
			.pipe(
				filter(event => event.type === 'VERSION_READY'),
				takeUntilDestroyed()
			)
			.subscribe(() => {
				if (performance.now() < UPDATE_BOOT_GRACE_MS || this.doc.visibilityState === 'hidden') {
					this.reload();
				} else {
					this.reloadPending = true;
				}
			});

		this.swUpdate.unrecoverable.pipe(takeUntilDestroyed()).subscribe(() => this.reload());

		fromEvent(this.doc, 'visibilitychange')
			.pipe(
				filter(() => this.reloadPending && this.doc.visibilityState === 'hidden'),
				takeUntilDestroyed()
			)
			.subscribe(() => this.reload());

		this.checkForUpdate();
	}

	/** A `true` answer also raises `VERSION_READY`, which is where it is acted on. */
	private checkForUpdate(): void {
		// Offline, or the worker is not yet registered: the next check will do.
		this.swUpdate.checkForUpdate().catch(() => false);
	}

	private reload(): void {
		this.doc.location.reload();
	}
}
